import type { NativeSelectOption } from '@shared/ui/fields'
import type { AppBadgeColor } from '@shared/ui/badges'
import type { DisciplineDetailsTutor } from './interfaces'
import type { Stage } from '@brighterly/lib-core-types/discipline/enums'

import { StudyingStatus } from '@brighterly/lib-core-types/discipline/enums'

import { useListsStore } from '@shared/model'
import { useAdminRoutes } from '@shared/lib/routes'

import { DisciplineTutorAssignType } from './enums'
import { useDisciplineStore } from './store'
import { useDisciplinePrivileges } from './discipline-privileges.hook'
import { useDisciplineGeneralForm } from './discipline-general-form.hook'
import {
  buildGradeOptions,
  buildStageOptions,
  formatLessonTime,
} from './discipline-general-info'

interface DisciplineStudyStatus {
  text: string
  color: AppBadgeColor
}

interface DisciplineGeneralInfoReturn {
  canViewDisciplines: ComputedRef<boolean>
  refStageOptions: ComputedRef<NativeSelectOption[]>
  refGradeOptions: ComputedRef<NativeSelectOption[]>
  refStage: ComputedRef<string | null>
  refGrade: ComputedRef<string | null>
  refIsManualAssign: ComputedRef<boolean>
  refIsAdditionalPractice: ComputedRef<boolean>
  refRegularTutorName: ComputedRef<string | null>
  refRegularTutorUrl: ComputedRef<string | null>
  refSubstituteTutors: ComputedRef<DisciplineDetailsTutor[]>
  refStudyStatus: ComputedRef<DisciplineStudyStatus | null>
  refFirstLessonTime: ComputedRef<string>
  refNextLessonTime: ComputedRef<string>
  setStage: (option: NativeSelectOption) => void
  setGrade: (option: NativeSelectOption) => void
  toggleManualAssign: () => void
  toggleAdditionalPractice: () => void
}

export const useDisciplineGeneralInfo = (): DisciplineGeneralInfoReturn => {
  const disciplineStore = useDisciplineStore()
  const listsStore = useListsStore()
  const { canViewDisciplines } = useDisciplinePrivileges()
  const { refForm, setField } = useDisciplineGeneralForm()

  const refDetails = computed(() => disciplineStore.getDetails)

  const refStage = computed<string | null>(() => refForm.value?.stage ?? null)
  const refGrade = computed<string | null>(() => refForm.value?.curriculum_grade ?? null)
  const refIsManualAssign = computed<boolean>(() => refForm.value?.manual_assign ?? false)
  const refIsAdditionalPractice = computed<boolean>(() => refForm.value?.additional_practice ?? false)

  const refStageOptions = computed<NativeSelectOption[]>(() => buildStageOptions(
    listsStore.getLists?.discipline.stage ?? {},
    refStage.value,
    !!refDetails.value?.tutor_id,
  ))

  const refGradeOptions = computed<NativeSelectOption[]>(() => buildGradeOptions(
    listsStore.getLists?.discipline.grade ?? {},
    refGrade.value,
  ))

  const refRegularTutor = computed<DisciplineDetailsTutor | null>(() => {
    const details = refDetails.value
    if (!details?.tutor_id) return null

    return details.tutors.find(tutor => tutor.id === details.tutor_id) ?? null
  })

  const refRegularTutorName = computed<string | null>(() => {
    const tutor = refRegularTutor.value
    return tutor ? `${tutor.first_name} ${tutor.last_name}` : null
  })

  const refRegularTutorUrl = computed<string | null>(() => {
    const tutorId = refDetails.value?.tutor_id
    return tutorId ? useAdminRoutes('admin.tutor.show', { tutor_id: tutorId }) : null
  })

  const refSubstituteTutors = computed<DisciplineDetailsTutor[]>(() =>
    (refDetails.value?.tutors ?? []).filter(tutor => tutor.assign_type === DisciplineTutorAssignType.Substitute))

  const refStudyStatus = computed<DisciplineStudyStatus | null>(() => {
    const status = refDetails.value?.studying_status
    if (!status) return null

    return {
      text: listsStore.getLists?.discipline.studying_status[status] ?? status,
      color: status === StudyingStatus.Active ? 'emerald' : 'red',
    }
  })

  const refFirstLessonTime = computed<string>(() => formatLessonTime(refDetails.value?.first_lesson_at ?? null))
  const refNextLessonTime = computed<string>(() => formatLessonTime(refDetails.value?.next_lesson_at ?? null))

  const setStage = (option: NativeSelectOption): void => {
    setField('stage', option.value === null ? null : String(option.value) as Stage)
  }

  const setGrade = (option: NativeSelectOption): void => {
    setField('curriculum_grade', option.value === null ? null : String(option.value))
  }

  const toggleManualAssign = (): void => {
    setField('manual_assign', !refIsManualAssign.value)
  }

  const toggleAdditionalPractice = (): void => {
    setField('additional_practice', !refIsAdditionalPractice.value)
  }

  return {
    canViewDisciplines,
    refStageOptions,
    refGradeOptions,
    refStage,
    refGrade,
    refIsManualAssign,
    refIsAdditionalPractice,
    refRegularTutorName,
    refRegularTutorUrl,
    refSubstituteTutors,
    refStudyStatus,
    refFirstLessonTime,
    refNextLessonTime,
    setStage,
    setGrade,
    toggleManualAssign,
    toggleAdditionalPractice,
  }
}
